/**
 * Shared task editing Sheet components used by both SmartPanel and Verkefnalisti.
 * Update these components in one place to affect both UIs.
 */

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { ReferralDeliveryPanel } from '@/features/referrals/ReferralDeliveryPanel';
import { useReferralState } from '@/features/referrals/useReferralState';
import { useGenerateDocument } from '@/lib/api';
import type { PrescriptionFormData } from '@/types/prescription';
import { useMutation } from '@tanstack/react-query';
import {
  Calendar,
  ChevronDown,
  Globe,
  Loader2,
  Mail,
  MessageSquareText,
  Pill,
  RefreshCw,
  Scan,
  Send,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { BookingSelector } from './BookingSelector';
import { CopyLetterButton } from './CopyLetterButton';
import { PrescriptionForm } from './PrescriptionForm';
import type { RequestLetterConfig, RequestLetterType } from './task-sheets.types';

// Common props for all task sheets
interface BaseTaskSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: () => void; // Called when task is confirmed/submitted
}

/** AI-detected task descriptions shorter than this are not full letters; auto-generate for those. */
const AUTO_GENERATE_MIN_LENGTH = 200;

/** Update an existing todo item's description via PATCH. */
async function updateTodoDescription(todoId: number, description: string, extraData?: Record<string, unknown>) {
  const res = await fetch(`/api/tasks/${todoId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ description, ...(extraData ? { extra_data: extraData } : {}) }),
  });
  if (!res.ok) throw new Error(`Failed to update todo ${todoId}: ${res.status}`);
}

// ============================================================================
// Prescription Sheet
// ============================================================================

interface PrescriptionSheetProps extends BaseTaskSheetProps {
  initialMedicationName?: string | null;
  initialDosageInfo?: string | null;
}

export function PrescriptionSheet({
  open,
  onOpenChange,
  onComplete,
  initialMedicationName,
  initialDosageInfo,
}: PrescriptionSheetProps) {
  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSubmit = (_data: PrescriptionFormData) => {
    // TODO: Send prescription to API
    onComplete?.();
    handleClose();
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <SheetContent side="center" className="max-w-3xl p-4">
        <SheetHeader className="mb-4">
          <SheetTitle className="flex items-center gap-2">
            <Pill className="h-5 w-5 text-primary" />
            Lyfseðill
          </SheetTitle>
        </SheetHeader>
        <div className="max-h-[70vh] overflow-y-auto">
          <PrescriptionForm
            initialMedicationName={initialMedicationName || undefined}
            initialDosageInfo={initialDosageInfo || undefined}
            onSubmit={handleSubmit}
            onClose={handleClose}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ============================================================================
// Booking Sheet
// ============================================================================

interface BookingSheetProps extends BaseTaskSheetProps {
  suggestedDate?: string | null;
  suggestedTime?: string | null;
}

export function BookingSheet({ open, onOpenChange, onComplete, suggestedDate, suggestedTime }: BookingSheetProps) {
  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSubmit = (_booking: {
    doctor: { id: string; name: string; specialty: string; color: string };
    date: string;
    time: string;
  }) => {
    // TODO: Send booking to API
    onComplete?.();
    handleClose();
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <SheetContent side="center" className="max-w-xl p-4">
        <SheetHeader className="mb-4">
          <SheetTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Endurbókun
          </SheetTitle>
        </SheetHeader>
        <div className="max-h-[70vh] overflow-y-auto">
          <BookingSelector
            suggestedDate={suggestedDate || undefined}
            suggestedTime={suggestedTime || undefined}
            onSubmit={handleSubmit}
            onClose={handleClose}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ============================================================================
// Patient Letter Sheet
// ============================================================================

interface PatientLetterSheetProps extends BaseTaskSheetProps {
  transcript?: string | null;
  sessionId?: string | null;
  patientId?: number | null;
  patientName?: string | null;
  initialContent?: string | null;
  /** DB todo item ID — when set, confirm updates the existing item instead of creating a new one */
  todoId?: number | null;
  /** Called when content is saved to DB (generation, edit confirm) so parent can update local state */
  onContentSaved?: (content: string) => void;
}

export function PatientLetterSheet({
  open,
  onOpenChange,
  onComplete,
  transcript,
  sessionId,
  patientId,
  patientName,
  initialContent,
  todoId,
  onContentSaved,
}: PatientLetterSheetProps) {
  const [letterContent, setLetterContent] = useState(initialContent ?? '');
  const [originalContent, setOriginalContent] = useState<string | null>(null);
  const [activeLanguage, setActiveLanguage] = useState<string | null>(null);
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const hasAutoGeneratedRef = useRef(false);

  const generateLetterMutation = useGenerateDocument({
    onMutate: () => {
      setOriginalContent(null);
      setActiveLanguage(null);
      setTranslations({});
    },
    onSuccess: (data) => {
      if (data.letter) {
        setLetterContent(data.letter);
        // Auto-save generated letter to DB and notify parent
        if (todoId) {
          updateTodoDescription(todoId, data.letter).catch((e) => console.error('Auto-save failed:', e));
        }
        onContentSaved?.(data.letter);
      }
    },
    onError: (error) => {
      console.error('Failed to generate patient letter:', error);
    },
  });

  const translateLetterMutation = useMutation({
    mutationFn: async (variables: { text: string; target_language: string }) => {
      const response = await fetch('/api/tasks/translate-letter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(variables),
      });
      if (!response.ok) throw new Error('Failed to translate letter');
      return response.json();
    },
    onSuccess: (data, variables) => {
      if (data.translated) {
        setTranslations((prev) => ({ ...prev, [variables.target_language]: data.translated }));
        setLetterContent(data.translated);
        setActiveLanguage(variables.target_language);
      }
    },
    onError: (error) => {
      console.error('Failed to translate letter:', error);
    },
  });

  const isGenerating = generateLetterMutation.isPending;
  const isTranslating = translateLetterMutation.isPending;

  // Reset state when the sheet opens with new content
  useEffect(() => {
    if (open) {
      setLetterContent(initialContent ?? '');
      setOriginalContent(null);
      setActiveLanguage(null);
      setTranslations({});
    }
  }, [open, initialContent]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleContentChange = (text: string) => {
    setLetterContent(text);
    if (activeLanguage) {
      // Editing a translation — update cache for this language
      setTranslations((prev) => ({ ...prev, [activeLanguage]: text }));
    } else {
      // Editing the original — invalidate all cached translations
      setTranslations({});
    }
  };

  const switchLanguage = (language: string) => {
    if (!letterContent.trim()) return;

    // Save current content before first translation
    if (!originalContent) {
      setOriginalContent(letterContent);
    } else if (!activeLanguage) {
      // Re-save original if we're on the Icelandic view and it was edited
      setOriginalContent(letterContent);
    }

    // Check cache
    if (translations[language]) {
      setLetterContent(translations[language]);
      setActiveLanguage(language);
      return;
    }

    // Translate from original (not from current translation)
    const sourceText = originalContent ?? letterContent;
    translateLetterMutation.mutate({ text: sourceText, target_language: language });
  };

  const restoreOriginal = () => {
    if (originalContent) {
      setLetterContent(originalContent);
      setActiveLanguage(null);
    }
  };

  const generateLetter = () => {
    if (!transcript) return;
    generateLetterMutation.mutate({
      type: 'patient_letter',
      transcript,
      session_id: sessionId,
      patient_id: patientId,
      patient_name: patientName,
    });
  };

  // Auto-generate on open if initialContent is just a short AI-detected description (not a full letter).
  // Guard the open cycle because saving the generated letter updates initialContent in the parent.
  // biome-ignore lint/correctness/useExhaustiveDependencies: generate on open only
  useEffect(() => {
    if (!open) {
      hasAutoGeneratedRef.current = false;
      return;
    }

    const isShortDescription = !initialContent || initialContent.length < AUTO_GENERATE_MIN_LENGTH;
    if (!hasAutoGeneratedRef.current && isShortDescription && transcript) {
      hasAutoGeneratedRef.current = true;
      generateLetter();
    }
  }, [open, initialContent, transcript]);

  const handleConfirm = async () => {
    if (!letterContent.trim()) return;

    if (todoId) {
      try {
        await updateTodoDescription(todoId, letterContent);
        onContentSaved?.(letterContent);
      } catch (error) {
        console.error('Failed to update patient letter:', error);
      }
    } else if (sessionId) {
      try {
        await fetch('/api/tasks', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            type: 'samantektarbréf',
            title: 'Samantektarbréf til skjólstæðings',
            description: letterContent,
            session_id: sessionId,
            patient_id: patientId,
          }),
        });
      } catch (error) {
        console.error('Failed to save patient letter:', error);
      }
    }

    onComplete?.();
    handleClose();
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <SheetContent
        side="center"
        aria-describedby={undefined}
        className="flex max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] max-w-2xl flex-col overflow-hidden p-4"
      >
        <SheetHeader className="mb-4 shrink-0 pr-10">
          <SheetTitle className="flex min-w-0 items-center gap-2 text-left">
            <Mail className="h-5 w-5 shrink-0 text-primary" />
            <span className="min-w-0">Samantektarbréf til skjólstæðings</span>
          </SheetTitle>
        </SheetHeader>
        <div data-testid="patient-letter-scroll-region" className="min-h-0 flex-1 space-y-4 overflow-y-auto">
          {isGenerating ? (
            <div className="flex min-h-[300px] flex-col items-center justify-center py-12">
              <Loader2 className="mb-3 h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground text-sm">Bý til bréf...</p>
            </div>
          ) : (
            <Textarea
              value={letterContent}
              onChange={(e) => handleContentChange(e.target.value)}
              placeholder="Bréfið birtist hér..."
              className="min-h-[300px] resize-none"
            />
          )}
        </div>
        <div data-testid="patient-letter-actions" className="mt-4 flex shrink-0 items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="icon"
              variant="outline"
              aria-label="Endurgera bréf"
              title="Endurgera bréf"
              onClick={generateLetter}
              disabled={isGenerating || isTranslating || !transcript}
            >
              {isGenerating ? <Loader2 className="animate-spin" /> : <RefreshCw />}
            </Button>
            <CopyLetterButton text={letterContent} disabled={isGenerating || isTranslating} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  aria-label="Þýða bréf"
                  title="Þýða bréf"
                  disabled={isGenerating || isTranslating || !letterContent.trim()}
                  className="w-10 gap-0.5"
                >
                  {isTranslating ? <Loader2 className="animate-spin" /> : <Globe />}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {activeLanguage && <DropdownMenuItem onClick={restoreOriginal}>Íslenska (frumtexti)</DropdownMenuItem>}
                <DropdownMenuItem disabled={activeLanguage === 'English'} onClick={() => switchLanguage('English')}>
                  English
                </DropdownMenuItem>
                <DropdownMenuItem disabled={activeLanguage === 'Polish'} onClick={() => switchLanguage('Polish')}>
                  Polski
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700"
            disabled={!letterContent.trim() || isGenerating || isTranslating}
            onClick={handleConfirm}
          >
            Staðfesta
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ============================================================================
// Request Letter Sheet (referral, consultant request, radiology request)
// ============================================================================

export const REQUEST_LETTER_CONFIG: Record<RequestLetterType, RequestLetterConfig> = {
  referral: {
    dbType: 'tilvísun',
    title: 'Tilvísun',
    icon: Send,
    generatingLabel: 'Bý til tilvísun...',
    placeholder: 'Tilvísunarbréfið birtist hér...',
    autoGenerateMinLength: AUTO_GENERATE_MIN_LENGTH,
    specialistPattern: /[Tt]ilvísun (?:á|til) (.+)/,
  },
  consultant_request: {
    dbType: 'ráðgjafabeiðni',
    title: 'Ráðgjafabeiðni',
    icon: MessageSquareText,
    generatingLabel: 'Bý til ráðgjafabeiðni...',
    placeholder: 'Ráðgjafabeiðnin birtist hér...',
    autoGenerateMinLength: AUTO_GENERATE_MIN_LENGTH,
    specialistPattern: /[Rr]áðgjafabeiðni (?:til|á) (.+)/,
  },
  radiology_request: {
    dbType: 'röntgenmyndir',
    title: 'Röntgenbeiðni',
    icon: Scan,
    generatingLabel: 'Bý til röntgenbeiðni...',
    placeholder: 'Röntgenbeiðnin birtist hér...',
    // A generated imaging request is a few labelled lines, far shorter than a letter.
    autoGenerateMinLength: 100,
  },
};

export const isRequestLetterType = (type: string): type is RequestLetterType =>
  Object.hasOwn(REQUEST_LETTER_CONFIG, type);

export const requestLetterTypeForDbType = (dbType: string): RequestLetterType | undefined =>
  (Object.keys(REQUEST_LETTER_CONFIG) as RequestLetterType[]).find(
    (type) => REQUEST_LETTER_CONFIG[type].dbType === dbType
  );

interface RequestLetterSheetProps extends BaseTaskSheetProps {
  requestType: RequestLetterType;
  initialTitle?: string | null;
  initialDetails?: string | null;
  /** Persisted generation state and original detected task details. */
  initialExtraData?: Record<string, unknown> | null;
  transcript?: string | null;
  sessionId?: string | null;
  patientId?: number | null;
  patientName?: string | null;
  /** DB todo item ID — when set, confirm updates the existing item instead of creating a new one */
  todoId?: number | null;
  /** Called when content is saved to DB (generation, edit confirm) so parent can update local state */
  onContentSaved?: (content: string, extraData: Record<string, unknown>) => void;
}

export const RequestLetterSheet = ({
  open,
  onOpenChange,
  onComplete,
  requestType,
  initialTitle,
  initialDetails,
  initialExtraData,
  transcript,
  sessionId,
  patientId,
  patientName,
  todoId,
  onContentSaved,
}: RequestLetterSheetProps) => {
  const referral = useReferralState(todoId ?? undefined, open && requestType === 'referral');
  const heklaMode = referral.visible;
  const referralBlocked =
    referral.locked ||
    (referral.featureEnabled &&
      (referral.probing || (referral.isError && (referral.error as { status?: number }).status !== 404)));
  const config = REQUEST_LETTER_CONFIG[requestType];
  const Icon = config.icon;
  const [letterContent, setLetterContent] = useState(initialDetails ?? '');
  const hasAutoGeneratedRef = useRef(false);
  const generationSheetKeyRef = useRef<string | null>(null);
  const activeSheetKeyRef = useRef<string | null>(null);
  const sheetKey = `${requestType}:${todoId ?? initialTitle ?? ''}`;
  const requestDocument = initialExtraData?.request_document;
  const requestMetadata =
    requestDocument && typeof requestDocument === 'object'
      ? (requestDocument as { source_details?: unknown; generated?: unknown })
      : null;
  const sourceDetails = typeof requestMetadata?.source_details === 'string' ? requestMetadata.source_details : '';
  const savedExtraData = {
    ...initialExtraData,
    request_document: { ...requestMetadata, source_details: sourceDetails, generated: true },
  };

  const generateMutation = useGenerateDocument({
    onSuccess: (data) => {
      const letter = data.letter;
      if (
        letter &&
        generationSheetKeyRef.current === activeSheetKeyRef.current &&
        activeSheetKeyRef.current === sheetKey
      ) {
        setLetterContent(letter);
        // Save document and generation state together so reopening cannot overwrite it.
        if (heklaMode) return;
        if (todoId) {
          updateTodoDescription(todoId, letter, savedExtraData)
            .then(() => onContentSaved?.(letter, savedExtraData))
            .catch((e) => console.error('Auto-save failed:', e));
        } else {
          onContentSaved?.(letter, savedExtraData);
        }
      }
    },
    onError: (error) => {
      console.error(`Failed to generate ${requestType}:`, error);
    },
  });

  const isGenerating = generateMutation.isPending;

  // Load saved text on open or when switching tasks, without replacing in-progress edits.
  useEffect(() => {
    if (!open) {
      activeSheetKeyRef.current = null;
    } else if (activeSheetKeyRef.current !== sheetKey) {
      activeSheetKeyRef.current = sheetKey;
      setLetterContent(initialDetails ?? '');
    }
  }, [open, sheetKey, initialDetails]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const generateLetter = () => {
    if (!transcript || referralBlocked) return;
    const specialist = (config.specialistPattern && (initialTitle || '').match(config.specialistPattern)?.[1]) || '';
    generationSheetKeyRef.current = sheetKey;
    generateMutation.mutate({
      type: requestType,
      transcript,
      session_id: sessionId,
      patient_id: patientId,
      patient_name: patientName,
      specialist,
      task_title: initialTitle,
      task_details: sourceDetails,
    });
  };

  // Only newly detected requests carry a persisted generated=false marker.
  // Existing requests without metadata stay untouched, including short legacy documents.
  // biome-ignore lint/correctness/useExhaustiveDependencies: generate on open only
  useEffect(() => {
    if (referralBlocked) return;
    if (!open) {
      // Keep the in-flight guard until the save updates the parent metadata.
      if (requestMetadata?.generated === true) hasAutoGeneratedRef.current = false;
      return;
    }

    const isShortDescription = !initialDetails || initialDetails.length < config.autoGenerateMinLength;
    if (!hasAutoGeneratedRef.current && requestMetadata?.generated === false && isShortDescription && transcript) {
      hasAutoGeneratedRef.current = true;
      generateLetter();
    }
  }, [open, initialDetails, initialExtraData, referralBlocked]);

  const handleConfirm = async () => {
    if (!letterContent.trim() || heklaMode || referralBlocked) return;

    if (todoId) {
      try {
        await updateTodoDescription(todoId, letterContent, savedExtraData);
        onContentSaved?.(letterContent, savedExtraData);
      } catch (error) {
        console.error(`Failed to update ${requestType}:`, error);
      }
    } else if (sessionId) {
      try {
        await fetch('/api/tasks', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            type: config.dbType,
            title: initialTitle || config.title,
            description: letterContent,
            extra_data: savedExtraData,
            session_id: sessionId,
            patient_id: patientId,
          }),
        });
      } catch (error) {
        console.error(`Failed to save ${requestType}:`, error);
      }
    }

    onComplete?.();
    handleClose();
  };

  const letterEditor = isGenerating ? (
    <div className="flex min-h-[300px] flex-col items-center justify-center py-12">
      <Loader2 className="mb-3 h-8 w-8 animate-spin text-primary" />
      <p className="text-muted-foreground text-sm">{config.generatingLabel}</p>
    </div>
  ) : (
    <Textarea
      disabled={referralBlocked}
      value={letterContent}
      onChange={(e) => setLetterContent(e.target.value)}
      placeholder={config.placeholder}
      aria-label={config.title}
      className="min-h-[300px] resize-none"
    />
  );

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <SheetContent
        side="center"
        aria-describedby={undefined}
        className="flex max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] max-w-2xl flex-col overflow-hidden p-4"
      >
        <SheetHeader className="mb-4 shrink-0 pr-10">
          <SheetTitle className="flex min-w-0 items-center gap-2 text-left">
            <Icon className="h-5 w-5 shrink-0 text-primary" />
            <span className="min-w-0">{config.title}</span>
          </SheetTitle>
        </SheetHeader>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
          {heklaMode && todoId ? (
            <ReferralDeliveryPanel
              key={sheetKey}
              taskId={todoId}
              sessionId={sessionId}
              letter={letterContent}
              editor={letterEditor}
              state={referral}
              onSaved={(draft) => {
                if (activeSheetKeyRef.current === sheetKey)
                  onContentSaved?.(draft.reason_for_referral, {
                    ...savedExtraData,
                    request_document: { ...savedExtraData.request_document, hekla: draft },
                  });
              }}
              onReload={(letter) => {
                if (activeSheetKeyRef.current === sheetKey) setLetterContent(letter);
              }}
            />
          ) : (
            letterEditor
          )}
          {referral.isError && referralBlocked && (
            <p role="alert">Ekki tókst að sækja stöðu tilvísunar. Reyndu aftur.</p>
          )}
        </div>
        <div className="mt-4 flex shrink-0 items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="icon"
              variant="outline"
              aria-label="Endurgera"
              title="Endurgera"
              onClick={generateLetter}
              disabled={isGenerating || !transcript || referralBlocked}
            >
              {isGenerating ? <Loader2 className="animate-spin" /> : <RefreshCw />}
            </Button>
            <CopyLetterButton text={letterContent} disabled={isGenerating} />
          </div>
          {!heklaMode && (
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={!letterContent.trim() || isGenerating || referralBlocked}
              onClick={handleConfirm}
            >
              Staðfesta
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
